import React from 'react';

import { DialogActions, InputLabel } from '@mui/material';
import { Formik } from 'formik';
import { AlineButton, AlineDialog, FormikAlineInput } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(theme => ({
  dialogActions: {
    paddingTop: theme.spacing(1),
  },
  dialogLabel: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

const savePromptValidationSchema = Yup.object({
  title: Yup.string().trim().required('Required').min(5, 'Title must be at least 5 characters'),
});

interface SavePromptDialogProps {
  onClose: () => void;
  open: boolean;
  promptText: string;
  onSave?: (promptId: string, text: string, title: string) => void;
  /** ID of the template prompt this was created from (e.g., Aline prompt saved to personal) */
  templatePromptId?: string | null;
}

export function SavePromptDialog({
  onClose,
  onSave,
  open,
  promptText,
  templatePromptId,
}: SavePromptDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [createSavedPrompt] = Apollo.useCreateSavedPromptMutation();

  const handleCreatePrompt = async (title: string) => {
    const text = promptText.trim();
    if (!text) return;

    const { data } = await createSavedPrompt({
      variables: {
        input: {
          scope: Apollo.SavedPromptScope.Personal,
          templatePromptId,
          text,
          title,
        },
      },
    });
    const newPromptId = data?.createSavedPrompt?.savedPrompt?.id;
    if (newPromptId) onSave?.(newPromptId, text, title);

    onClose();
  };

  return (
    <AlineDialog
      fullWidth
      maxWidth="xs"
      open={open}
      title="Save prompt?"
      onClick={e => e.stopPropagation()}
      onClose={onClose}
    >
      <Formik
        initialValues={{ title: '' }}
        validationSchema={savePromptValidationSchema}
        onSubmit={async ({ title }) => {
          await handleCreatePrompt(title.trim());
        }}
      >
        {({ dirty, handleReset, handleSubmit, isSubmitting, isValid }): JSX.Element => (
          <form onReset={handleReset} onSubmit={handleSubmit}>
            <InputLabel className={classes.dialogLabel}>Prompt Title</InputLabel>
            <FormikAlineInput
              autoFocus
              fieldName="title"
              placeholder="Type a prompt title..."
              onKeyDown={(event: React.KeyboardEvent): void => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <DialogActions className={classes.dialogActions}>
              <AlineButton color="primary" variant="outlined" onClick={onClose}>
                Cancel
              </AlineButton>
              <AlineButton
                color="primary"
                disabled={!dirty || !isValid}
                loading={isSubmitting}
                type="submit"
                variant="contained"
              >
                Save
              </AlineButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </AlineDialog>
  );
}
